import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import axiosInstance from '../services/axios.config';
import { publishMissionPreview, CompiledMission } from '../services/mission-preview';

interface AuditProps {
    onClose?: () => void;
}

function Audit({ onClose }: AuditProps): React.ReactElement {

    const [fullAudit, setFullAudit] = useState(true);
    const [sectionAudit, setSectionAudit] = useState(false);
    const [section, setSection] = useState('');

    const [runNow, setRunNow] = useState(true);
    const [every30, setEvery30] = useState(false);
    const [hourly, setHourly] = useState(false);

    const [message, setMessage] = useState<string | null>(null);

    const handleClose = () => {
        onClose?.();
    };

    const handleFullAudit = (checked: boolean) => {
        setFullAudit(checked);

        if (checked) {
            setSectionAudit(false);
            setSection('');
        }
    };

    const handleSectionAudit = (checked: boolean) => {
        setSectionAudit(checked);

        if (checked) {
            setFullAudit(false);
        } else {
            setSection('');
        }
    };

    const handleSubmit = async () => {
        if (!fullAudit && !sectionAudit) {
            setMessage('Select Full Yard Audit or Section Audit.');
            return;
        }

        if (sectionAudit && !section.trim()) {
            setMessage('Enter the section you want to audit.');
            return;
        }

        if (!runNow && !every30 && !hourly) {
            setMessage('Select at least one schedule option.');
            return;
        }

        if (every30 || hourly) {
            setMessage(
                'Recurring audit scheduling is not available yet. ' +
                'Select Run Now to generate a mission for review.'
            );
            return;
        }

        setMessage('Generating Yard Audit mission...');

        try {
            const response = await axiosInstance.post(
                '/yards/audit/generate',
                {
                    full_audit: fullAudit,
                    sections: sectionAudit ? [section.trim()] : undefined
                }
            );

            if (response.data?.success !== true || !response.data?.mission) {
                throw new Error('Mission generation was unsuccessful');
            }

            const compiled = await axiosInstance.post<CompiledMission>(
                '/yards/mission/compile',
                { mission: response.data.mission }
            );

            publishMissionPreview(compiled.data);

            setMessage(
                'Yard Audit mission generated and validated. ' +
                'Flight preview is ready. No drone has been dispatched.'
            );
        } catch (error) {
            console.error('Yard Audit generation failed:', error);

            setMessage(
                'Unable to generate Yard Audit mission. ' +
                'Please check the yard configuration or contact support.'
            );
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4">

            <div className="w-full max-w-[720px] rounded-lg bg-white shadow-xl">

                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">

                    <div>
                        <h2 className="text-2xl font-bold text-black-1 font-[Geist-Bold]">
                            Yard Audit
                        </h2>

                        <p className="mt-1 text-sm text-gray-500">
                            Configure a full yard or section audit
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-gray-500 hover:text-black"
                    >
                        <Icon
                            icon="gridicons:cross"
                            width={28}
                            height={28}
                        />
                    </button>

                </div>

                {/* Main Content */}
                <div className="p-6">

                    {/* Audit Type */}
                    <div>
                        <h3 className="mb-3 text-base font-semibold text-black-1">
                            Audit Type
                        </h3>

                        <div className="space-y-3">

                            <label className="flex cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={fullAudit}
                                    onChange={(e) => handleFullAudit(e.target.checked)}
                                />

                                <div>
                                    <span className="font-medium">
                                        Full Yard Audit
                                    </span>

                                    <p className="text-sm text-gray-500">
                                        Audit the entire configured yard.
                                    </p>
                                </div>
                            </label>

                            <label className="flex cursor-pointer items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={sectionAudit}
                                    onChange={(e) => handleSectionAudit(e.target.checked)}
                                />

                                <div>
                                    <span className="font-medium">
                                        Section Audit
                                    </span>

                                    <p className="text-sm text-gray-500">
                                        Audit a specific section of the yard.
                                    </p>
                                </div>
                            </label>

                        </div>

                        {sectionAudit && (
                            <div className="mt-4">

                                <label
                                    htmlFor="auditSection"
                                    className="mb-2 block text-sm font-semibold text-black-1"
                                >
                                    Which Section?
                                </label>

                                <input
                                    id="auditSection"
                                    type="text"
                                    value={section}
                                    onChange={(e) => setSection(e.target.value)}
                                    placeholder="Enter yard section"
                                    className="w-full rounded border border-gray-300 p-3 outline-none focus:border-blue"
                                />

                            </div>
                        )}
                    </div>

                    {/* Schedule */}
                    <div className="mt-7">

                        <h3 className="mb-3 text-base font-semibold text-black-1">
                            Schedule
                        </h3>

                        <div className="grid grid-cols-1 gap-3 rounded border border-gray-200 p-4 sm:grid-cols-3">

                            <label className="flex cursor-pointer items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={runNow}
                                    onChange={(e) => setRunNow(e.target.checked)}
                                />
                                <span>Now</span>
                            </label>

                            <label className="flex cursor-pointer items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={every30}
                                    onChange={(e) => setEvery30(e.target.checked)}
                                />
                                <span>Every 30 Minutes</span>
                            </label>

                            <label className="flex cursor-pointer items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={hourly}
                                    onChange={(e) => setHourly(e.target.checked)}
                                />
                                <span>Every Hour</span>
                            </label>

                        </div>

                    </div>

                    {/* Mission Status */}
                    <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">

                        <div className="flex items-center justify-between gap-4">

                            <div>
                                <p className="font-semibold text-black-1">
                                    Yard Audit Mission
                                </p>

                                <p className="mt-1 text-sm text-gray-500">
                                    Drone mission integration is not connected yet.
                                </p>
                            </div>

                            <span className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-600">
                                Not Connected
                            </span>

                        </div>

                    </div>

                    {message && (
                        <div className="mt-4 rounded border border-gray-200 bg-gray-50 px-4 py-3">
                            <p className="text-sm text-gray-600">
                                {message}
                            </p>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t px-6 py-4">

                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded px-5 py-2 text-gray-600 hover:bg-gray-100"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="rounded bg-blue px-6 py-2 font-semibold text-white hover:opacity-90"
                    >
                        Submit Audit
                    </button>

                </div>

            </div>

        </div>
    );
}

export default Audit;
